import { useState, useEffect, useMemo } from "react";
import { Loader, X, FileText, AlertTriangle, CheckCircle, RotateCcw, Shield, ShieldAlert, ShieldCheck, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getErrorMessage } from "../lib/api";
import { fmt } from "../lib/utils";
import ConfirmDialog from "./common/ConfirmDialog";
import StatusDot from "./common/StatusDot";
import Sheet from "./common/Sheet";

export default function SikkerhetsView() {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const [showKoblForm, setShowKoblForm] = useState<string | null>(null);
  const [koblForm, setKoblForm] = useState({ project_id: "", contract_id: "" });
  const [showGodkjennForm, setShowGodkjennForm] = useState<string | null>(null);
  const [godkjennEmail, setGodkjennEmail] = useState("");
  const [confirm, setConfirm] = useState<{ invoiceId: string; type: "avvis" | "svindel" } | null>(null);
  const [pdfModal, setPdfModal] = useState<{ url: string; title: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [activeFilter, setActiveFilter] = useState<"alle" | "ukjent" | "karantene">("alle");

  const showMsg = (text: string, ok: boolean) => {
    setMessage({ text, ok });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        api.get("/portfolio/sikkerhet"),
        api.get("/projects/"),
      ]);
      setData(sRes.data);
      setProjects(pRes.data.projects);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const openPdfModal = async (invoiceId: string, title: string) => {
    try {
      const res = await api.get(`/portfolio/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfModal({ url, title });
    } catch (e: any) {
      showMsg(getErrorMessage(e, "Kunne ikke laste PDF"), false);
    }
  };
  const closePdfModal = () => {
    if (pdfModal) URL.revokeObjectURL(pdfModal.url);
    setPdfModal(null);
  };

  const godkjennAvsender = async (invoiceId: string) => {
    if (!godkjennEmail) return;
    setActionLoading(invoiceId);
    try {
      const res = await api.post(`/portfolio/sikkerhet/godkjenn-avsender/${invoiceId}`, { email: godkjennEmail });
      showMsg(res.data.message, true);
      setShowGodkjennForm(null); setGodkjennEmail("");
      fetchData();
    } catch (e: any) {
      showMsg(getErrorMessage(e, "Feil ved godkjenning"), false);
    } finally { setActionLoading(null); }
  };

  const koblTilProsjekt = async (invoiceId: string) => {
    if (!koblForm.project_id || !koblForm.contract_id) return;
    setActionLoading(invoiceId);
    try {
      const res = await api.post(`/portfolio/sikkerhet/koble-til-prosjekt/${invoiceId}`, koblForm);
      showMsg(res.data.message, true);
      setShowKoblForm(null); setKoblForm({ project_id: "", contract_id: "" });
      fetchData();
    } catch (e: any) {
      showMsg(getErrorMessage(e, "Feil ved kobling"), false);
    } finally { setActionLoading(null); }
  };

  const utforHandling = async (invoiceId: string, type: "avvis" | "svindel") => {
    setActionLoading(invoiceId);
    try {
      await api.post(`/portfolio/sikkerhet/${type}/${invoiceId}`, {});
      showMsg(type === "svindel" ? "Faktura markert som svindelforsøk" : "Faktura avvist", true);
      fetchData();
      setSelectedId(null);
    } catch (e: any) {
      showMsg(getErrorMessage(e), false);
    } finally { setActionLoading(null); setConfirm(null); }
  };

  const angreHandling = async (invoiceId: string) => {
    setActionLoading(invoiceId);
    try {
      await api.post(`/portfolio/sikkerhet/angre/${invoiceId}`, {});
      showMsg("Handling angret — faktura tilbake i karantene", true);
      fetchData();
    } catch (e: any) {
      showMsg(getErrorMessage(e), false);
    } finally { setActionLoading(null); }
  };

  const getContractsForProject = (projectId: string) => projects.find(p => p.id === projectId)?.vendors || [];

  const ukjentAvsender = data?.ukjent_avsender || [];
  const karantene = data?.karantene || [];
  const logg = data?.logg || [];
  const totalt = ukjentAvsender.length + karantene.length;

  const allCases = useMemo(() => {
    const list: any[] = [];
    ukjentAvsender.forEach((inv: any) => list.push({ ...inv, _category: "ukjent" }));
    karantene.forEach((inv: any) => list.push({ ...inv, _category: "karantene" }));
    return list;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCases = useMemo(() => {
    if (activeFilter === "alle") return allCases;
    return allCases.filter(c => c._category === activeFilter);
  }, [allCases, activeFilter]);

  useEffect(() => {
    if (!selectedId && filteredCases.length > 0) { setSelectedId(filteredCases[0].id); return; }
    if (selectedId && !filteredCases.find(c => c.id === selectedId) && !logg.find((l: any) => l.id === selectedId)) {
      setSelectedId(filteredCases[0]?.id || null);
    }
  }, [filteredCases, selectedId, logg]);

  const selected = useMemo(() => {
    return [...allCases, ...logg].find((c: any) => c.id === selectedId);
  }, [allCases, logg, selectedId]);

  // Visuelt tema per kategori
  const theme = (c: any) => {
    if (c?._category === "ukjent") return { icon: ShieldAlert, ring: "bg-flag/10", fg: "text-flag", dot: "bg-flag", label: t("security.mistenkelig_avsender") };
    if (c?._category === "karantene") return { icon: Shield, ring: "bg-neg/10", fg: "text-neg", dot: "bg-neg", label: t("security.karantene_ukjent") };
    if (c?.status === "svindel") return { icon: AlertTriangle, ring: "bg-neg/10", fg: "text-neg", dot: "bg-neg", label: t("security.svindelforsok") };
    return { icon: X, ring: "bg-black/[0.05]", fg: "text-muted", dot: "bg-faint", label: t("common.avvist") };
  };

  if (loading && !data) return (
    <div className="flex flex-col items-center justify-center py-32 gap-3">
      <Loader className="w-6 h-6 animate-spin text-faint" />
      <span className="text-[13px] text-muted">{t("security.laster")}</span>
    </div>
  );

  return (
    <div className="flex gap-0 -mx-4 sm:-mx-6 md:-mx-8 -my-6 md:-my-8 min-h-[calc(100vh-56px)]">
      {confirm && (
        <ConfirmDialog
          title={confirm.type === "svindel" ? t("security.marker_svindel_title") : t("security.avvis_faktura_title")}
          description={confirm.type === "svindel" ? t("security.marker_svindel_desc") : t("security.avvis_faktura_desc")}
          confirmLabel={confirm.type === "svindel" ? t("security.ja_marker_svindel") : t("security.ja_avvis")}
          confirmColor={confirm.type === "svindel" ? "bg-neg hover:opacity-90" : "bg-night hover:opacity-90"}
          onConfirm={() => utforHandling(confirm.invoiceId, confirm.type)}
          onCancel={() => setConfirm(null)}
        />
      )}

      {message && (
        <div className={`fixed top-[64px] right-6 z-[110] rounded-[12px] px-4 py-3 text-[13px] font-semibold shadow-pop ${message.ok ? "bg-pos text-white" : "bg-neg text-white"}`}>
          {message.text}
        </div>
      )}

      {/* ── VENSTRE PANEL: Saksliste ── */}
      <aside className={`${mobileDetail ? "hidden md:flex" : "flex"} w-full md:w-[300px] shrink-0 border-r border-line flex-col overflow-hidden bg-panel/40`}>
        <div className="px-5 pt-6 pb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-night" />
            <h1 className="text-[20px] font-bold text-night">{t("security.sikkerhet")}</h1>
          </div>
          <p className="text-[12.5px] text-muted mt-1">
            {totalt === 0 ? t("security.alt_bra") : t("security.krever_handling_count", { count: totalt })}
          </p>
        </div>

        {/* Filter pills */}
        <div className="px-3 space-y-1 mb-3">
          {[
            { id: "alle" as const, label: t("security.krever_handling"), count: totalt, color: "red" },
            { id: "ukjent" as const, label: t("security.mistenkelig"), count: ukjentAvsender.length, color: "amb" },
            { id: "karantene" as const, label: t("security.karantene"), count: karantene.length, color: "ink" },
          ].map((f) => (
            <button key={f.id} onClick={() => setActiveFilter(f.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-[10px] text-[13px] font-medium transition-colors ${
                activeFilter === f.id ? "bg-night/[0.06] text-night font-semibold" : "text-muted hover:bg-black/[0.04]"}`}>
              <div className="flex items-center gap-2.5">
                <StatusDot color={f.color as any} size="sm" pulse={f.count > 0} />
                <span>{f.label}</span>
              </div>
              <span className={`text-[12px] font-bold tabular ${activeFilter === f.id ? "text-night" : "text-faint"}`}>{f.count}</span>
            </button>
          ))}
        </div>

        <div className="border-t border-line flex-1 overflow-y-auto">
          {filteredCases.length === 0 ? (
            <p className="text-[13px] text-faint text-center py-10 px-5">{t("security.ingen_saker")}</p>
          ) : (
            <>
              <p className="text-eyebrow uppercase text-faint px-5 pt-5 pb-2">{t("security.til_vurdering")}</p>
              {filteredCases.map((inv: any) => {
                const active = selectedId === inv.id;
                const dotColor = inv._category === "ukjent" ? "amb" : "red";
                return (
                  <button key={inv.id} onClick={() => { setSelectedId(inv.id); setMobileDetail(true); }}
                    className={`w-full text-left px-5 py-3 transition-colors ${active ? "bg-black/[0.05]" : "hover:bg-black/[0.03]"}`}>
                    <div className="flex items-start gap-2.5">
                      <div className="mt-1.5"><StatusDot color={dotColor as any} size="sm" pulse={false} /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-bold tabular text-night truncate">{inv.invoice_number}</p>
                        <p className="text-[12px] text-muted truncate">{inv.vendor_name}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {logg.length > 0 && (
            <div className="mt-3 pb-6">
              <p className="text-eyebrow uppercase text-faint px-5 pt-5 pb-2">{t("security.behandlet")}</p>
              {logg.slice(0, 10).map((inv: any) => (
                <button key={inv.id} onClick={() => { setSelectedId(inv.id); setMobileDetail(true); }}
                  className={`w-full text-left px-5 py-2.5 opacity-70 hover:opacity-100 transition-opacity ${selectedId === inv.id ? "bg-black/[0.05] !opacity-100" : ""}`}>
                  <div className="flex items-center gap-2.5">
                    {inv.status === "svindel" ? <X className="w-3.5 h-3.5 text-neg shrink-0" />
                      : inv.status === "avvist" ? <X className="w-3.5 h-3.5 text-faint shrink-0" />
                      : <CheckCircle className="w-3.5 h-3.5 text-pos shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-night tabular truncate">{inv.invoice_number}</p>
                      <p className="text-[11px] text-faint truncate">{inv.vendor_name} · {inv.status === "svindel" ? t("security.svindel") : t("common.avvist")}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </aside>

      {/* ── HØYRE PANEL: Detalj ── */}
      <main className={`${mobileDetail ? "flex" : "hidden md:flex"} flex-1 overflow-y-auto bg-canvas min-w-0 flex-col`}>
        {!selected ? (
          <div className="h-full flex items-center justify-center p-6">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-pos/10 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-pos" />
              </div>
              <p className="text-[16px] font-bold text-night mb-1">{t("security.alt_bra")}</p>
              <p className="text-[13px] text-muted">{t("security.ingen_sikkerhetsvurdering")}</p>
            </div>
          </div>
        ) : (() => {
          const th = theme(selected);
          const Icon = th.icon;
          const risk = selected.analysis?.risikonivaa;
          const handled = selected.status === "avvist" || selected.status === "svindel";
          return (
            <div className="max-w-[680px] mx-auto px-4 sm:px-6 md:px-8 pt-5 md:pt-8 pb-12 animate-fade-up w-full" key={selected.id}>
              <button onClick={() => setMobileDetail(false)} className="md:hidden mb-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted hover:text-night">
                <ArrowLeft className="w-4 h-4" /> {t("common.tilbake")}
              </button>

              {/* Header-kort */}
              <div className="cx-card p-5 mb-4">
                <div className="flex items-start gap-3.5">
                  <div className={`w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0 ${th.ring}`}>
                    <Icon className={`w-5 h-5 ${th.fg}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-line bg-canvas text-muted">
                        <span className={`w-1.5 h-1.5 rounded-full ${th.dot}`} />
                        {th.label}
                      </span>
                      {risk && (
                        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full border border-line bg-canvas text-muted">
                          <span className={`w-1.5 h-1.5 rounded-full ${risk === "HØY" ? "bg-neg" : risk === "MIDDELS" ? "bg-flag" : "bg-pos"}`} />
                          {risk} {t("security.risiko")}
                        </span>
                      )}
                    </div>
                    <h2 className="text-[22px] font-bold text-night tabular mt-2 break-all leading-tight">{selected.invoice_number}</h2>
                    <p className="text-[14px] font-semibold text-night mt-1 break-words">{selected.vendor_name || t("security.ukjent_leverandor")}</p>
                    <p className="text-[12.5px] text-muted mt-0.5">
                      {selected._category === "ukjent" ? t("security.kjent_leverandor") : selected._category === "karantene" ? t("security.ikke_registrert") : t("security.behandlet")}
                      {selected.created_at && ` · ${new Date(selected.created_at).toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" })}`}
                    </p>
                  </div>
                  {selected.total_amount != null && (
                    <div className="text-right shrink-0">
                      <p className="text-[18px] font-bold text-night tabular leading-none">{fmt(selected.total_amount, selected.currency || "NOK")}</p>
                      {selected.analysis?.avvik > 0 && (
                        <p className="text-[13px] font-bold text-neg tabular mt-1">−{fmt(selected.analysis.avvik, selected.currency || "NOK")}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Årsak-kort */}
              {(selected.analysis?.avvik_forklaring || selected._category === "karantene" || selected.sender_email) && (
                <div className="cx-card p-5 mb-4">
                  <h3 className="text-[12px] font-bold uppercase tracking-[0.04em] text-faint mb-2.5">{t("security.arsak_flagging")}</h3>
                  <div className="text-[13px] text-night leading-relaxed space-y-2">
                    {selected._category === "karantene" && (
                      <p>{t("security.arsak_karantene_pre")} <strong>{selected.vendor_name}</strong> {t("security.arsak_karantene_post")}</p>
                    )}
                    {selected._category === "ukjent" && selected.sender_email && (
                      <p>{t("security.arsak_ukjent_pre")} <strong>{selected.sender_email}</strong> {t("security.arsak_ukjent_post", { vendor: selected.vendor_name })}</p>
                    )}
                    {selected.analysis?.avvik_forklaring && <p className="text-muted">{selected.analysis.avvik_forklaring}</p>}
                  </div>
                </div>
              )}

              {/* Handlinger */}
              {(selected._category === "ukjent" || selected._category === "karantene") && (
                <div className="cx-card p-4">
                  <div className="space-y-2.5">
                    {selected._category === "ukjent" ? (
                      <button onClick={() => { setShowGodkjennForm(selected.id); setGodkjennEmail(selected.sender_email || ""); }} className="cx-btn-dark w-full !py-2.5 !text-[14px]">
                        <CheckCircle className="w-4 h-4" /> {t("security.godkjenn_avsender")}
                      </button>
                    ) : (
                      <button onClick={() => setShowKoblForm(selected.id)} className="cx-btn-dark w-full !py-2.5 !text-[14px]">
                        <FileText className="w-4 h-4" /> {t("security.koble_til_prosjekt")}
                      </button>
                    )}

                    <div className="grid grid-cols-2 gap-2.5">
                      <button onClick={() => openPdfModal(selected.id, selected.invoice_number)} className="cx-btn-ghost !py-2.5 !text-[14px]">
                        <FileText className="w-4 h-4" /> {t("security.se_faktura")}
                      </button>
                      <button onClick={() => setConfirm({ invoiceId: selected.id, type: "avvis" })} className="cx-btn-ghost !py-2.5 !text-[14px]">
                        <X className="w-4 h-4" /> {t("security.avvis")}
                      </button>
                    </div>
                  </div>

                  {/* Faresone — lavmælt, ikke en stor rød boks */}
                  <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-line">
                    <span className="text-[12px] text-faint">{t("security.faresone")}</span>
                    <button onClick={() => setConfirm({ invoiceId: selected.id, type: "svindel" })}
                      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neg hover:opacity-70 transition-opacity">
                      <AlertTriangle className="w-3.5 h-3.5" /> {t("security.marker_svindel")}
                    </button>
                  </div>
                </div>
              )}

              {handled && (
                <div className="cx-card p-4 space-y-2.5">
                  <button onClick={() => openPdfModal(selected.id, selected.invoice_number)} className="cx-btn-ghost w-full !py-2.5 !text-[14px]">
                    <FileText className="w-4 h-4" /> {t("security.se_faktura")}
                  </button>
                  <button onClick={() => angreHandling(selected.id)} disabled={actionLoading === selected.id} className="cx-btn-ghost w-full !py-2.5 !text-[14px]">
                    {actionLoading === selected.id ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    {t("security.angre_handling")}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </main>

      {/* Sheet: Godkjenn avsender */}
      <Sheet open={showGodkjennForm !== null} onClose={() => { setShowGodkjennForm(null); setGodkjennEmail(""); }}
        title={t("security.godkjenn_avsender_title")}
        subtitle={selected ? t("security.godkjenn_avsender_sub", { vendor: selected.vendor_name }) : ""}>
        <div className="space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("security.epostadresse")}</label>
            <input value={godkjennEmail} onChange={e => setGodkjennEmail(e.target.value)}
              placeholder="faktura@leverandør.no" className="field-ios" autoFocus />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-1 border-t border-line -mx-6 px-6">
            <button onClick={() => { setShowGodkjennForm(null); setGodkjennEmail(""); }} className="cx-btn-ghost !px-5 !py-2.5">{t("common.avbryt")}</button>
            <button onClick={() => showGodkjennForm && godkjennAvsender(showGodkjennForm)} disabled={!godkjennEmail || actionLoading === showGodkjennForm} className="cx-btn-dark !px-5 !py-2.5">
              {actionLoading === showGodkjennForm ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {t("security.godkjenn_avsender")}
            </button>
          </div>
        </div>
      </Sheet>

      {/* Sheet: Koble til prosjekt */}
      <Sheet open={showKoblForm !== null} onClose={() => { setShowKoblForm(null); setKoblForm({ project_id: "", contract_id: "" }); }}
        title={t("security.koble_prosjekt_title")}
        subtitle={t("security.koble_prosjekt_sub")}>
        <div className="space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("security.prosjekt")}</label>
            <select value={koblForm.project_id} onChange={e => setKoblForm({ project_id: e.target.value, contract_id: "" })} className="field-ios">
              <option value="">{t("security.velg_prosjekt")}</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("invoice.leverandor")}</label>
            <select value={koblForm.contract_id} onChange={e => setKoblForm({ ...koblForm, contract_id: e.target.value })} className="field-ios" disabled={!koblForm.project_id}>
              <option value="">{t("security.velg_leverandor")}</option>
              {getContractsForProject(koblForm.project_id).map((v: any) => (
                <option key={v.contract_id} value={v.contract_id}>{v.vendor_name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-1 border-t border-line -mx-6 px-6">
            <button onClick={() => { setShowKoblForm(null); setKoblForm({ project_id: "", contract_id: "" }); }} className="cx-btn-ghost !px-5 !py-2.5">{t("common.avbryt")}</button>
            <button onClick={() => showKoblForm && koblTilProsjekt(showKoblForm)} disabled={!koblForm.project_id || !koblForm.contract_id || actionLoading === showKoblForm} className="cx-btn-dark !px-5 !py-2.5">
              {actionLoading === showKoblForm ? <Loader className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {t("security.koble_til_prosjekt")}
            </button>
          </div>
        </div>
      </Sheet>

      {/* PDF modal */}
      {pdfModal && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={closePdfModal}>
          <div className="bg-card rounded-[16px] shadow-pop border border-line w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
              <p className="text-[14px] font-bold text-night tabular">{t("security.faktura")} {pdfModal.title}</p>
              <button onClick={closePdfModal} className="w-8 h-8 rounded-[9px] flex items-center justify-center text-faint hover:text-night hover:bg-black/[0.05] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe src={pdfModal.url} className="flex-1 w-full" title="Faktura PDF" />
          </div>
        </div>
      )}
    </div>
  );
}
